import { Capacitor } from '@capacitor/core';
import { formatCOP } from '../services/api';

function safeName(s) {
  return String(s || 'pedido')
    .replace(/[^\w\-.#]+/g, '_')
    .slice(0, 60);
}

function lineItems(items = []) {
  return (items || []).map((it) => {
    const name = String(it.nombre || it.codigo || 'Producto').trim();
    const qty = Number(it.cantidad) || 0;
    const price = Number(it.precio_unitario) || 0;
    const sub = Number(it.subtotal ?? price * qty) || 0;
    return { name, qty, price, sub, code: it.codigo || '' };
  });
}

/** Build a printable invoice PDF (Blob) from pedido snapshot data. */
export async function buildFacturaPdfBlob({
  numero,
  clienteNombre,
  clienteTelefono,
  items = [],
  subtotal,
  offline = false,
} = {}) {
  const { jsPDF } = await import('jspdf');
  const doc = new jsPDF({ unit: 'pt', format: 'letter' });
  const margin = 48;
  let y = margin;
  const pageW = doc.internal.pageSize.getWidth();
  const maxW = pageW - margin * 2;

  const numLabel = numero
    ? String(numero)
    : offline
      ? '(sin enviar)'
      : 'vista previa';

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(16);
  doc.setTextColor(233, 30, 150);
  doc.text('Oh Belleza — Pedido', margin, y);
  y += 22;

  doc.setTextColor(24, 24, 27);
  doc.setFontSize(12);
  doc.text(`Nº ${numLabel}`, margin, y);
  y += 16;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.setTextColor(113, 113, 122);
  doc.text(new Date().toLocaleString('es-CO'), margin, y);
  y += 22;

  doc.setTextColor(24, 24, 27);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.text('Cliente', margin, y);
  y += 14;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(11);
  const cliente = String(clienteNombre || 'Cliente').trim() || 'Cliente';
  doc.text(doc.splitTextToSize(cliente, maxW), margin, y);
  y += 14;
  if (clienteTelefono) {
    doc.setFontSize(10);
    doc.setTextColor(82, 82, 91);
    doc.text(`Tel: ${clienteTelefono}`, margin, y);
    y += 16;
  }
  y += 8;

  doc.setDrawColor(233, 30, 150);
  doc.setLineWidth(1);
  doc.line(margin, y, pageW - margin, y);
  y += 18;

  doc.setTextColor(24, 24, 27);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.text('Ítem', margin, y);
  doc.text('Cant.', pageW - margin - 140, y, { align: 'right' });
  doc.text('Subtotal', pageW - margin, y, { align: 'right' });
  y += 12;
  doc.setDrawColor(228, 228, 231);
  doc.line(margin, y, pageW - margin, y);
  y += 14;

  doc.setFont('helvetica', 'normal');
  const rows = lineItems(items);
  for (const row of rows) {
    if (y > 700) {
      doc.addPage();
      y = margin;
    }
    const title = row.code ? `${row.code} — ${row.name}` : row.name;
    const lines = doc.splitTextToSize(title, maxW - 160);
    doc.setFontSize(10);
    doc.setTextColor(24, 24, 27);
    doc.text(lines, margin, y);
    doc.text(String(row.qty), pageW - margin - 140, y, { align: 'right' });
    doc.text(formatCOP(row.sub), pageW - margin, y, { align: 'right' });
    y += Math.max(14, lines.length * 12) + 6;
  }

  y += 8;
  doc.setDrawColor(233, 30, 150);
  doc.line(margin, y, pageW - margin, y);
  y += 18;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(13);
  doc.setTextColor(233, 30, 150);
  const total =
    subtotal != null && !Number.isNaN(Number(subtotal))
      ? Number(subtotal)
      : rows.reduce((s, r) => s + r.sub, 0);
  doc.text(`Total: ${formatCOP(total)}`, pageW - margin, y, { align: 'right' });

  return doc.output('blob');
}

async function shareNativeFile(blob, fileName, text) {
  const { Directory, Filesystem } = await import('@capacitor/filesystem');
  const { Share } = await import('@capacitor/share');
  const reader = new FileReader();
  const base64 = await new Promise((resolve, reject) => {
    reader.onload = () => {
      const dataUrl = String(reader.result || '');
      const i = dataUrl.indexOf(',');
      resolve(i >= 0 ? dataUrl.slice(i + 1) : dataUrl);
    };
    reader.onerror = () => reject(reader.error || new Error('No se pudo leer el PDF'));
    reader.readAsDataURL(blob);
  });
  const path = `pedidos/${fileName}`;
  await Filesystem.mkdir({ path: 'pedidos', directory: Directory.Cache, recursive: true }).catch(
    () => {}
  );
  await Filesystem.writeFile({ path, data: base64, directory: Directory.Cache });
  const { uri } = await Filesystem.getUri({ path, directory: Directory.Cache });
  await Share.share({
    title: fileName,
    text: text || 'Pedido Oh Belleza',
    url: uri,
    dialogTitle: 'Enviar pedido (PDF)',
  });
}

function downloadBlob(blob, fileName) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  a.rel = 'noopener';
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 4000);
}

/**
 * Generate factura PDF and open the system share sheet (WhatsApp, etc.).
 * Falls back to download + WhatsApp text if file share is unavailable.
 */
export async function shareFacturaPdf(opts = {}) {
  const blob = await buildFacturaPdfBlob(opts);
  const fileName = `pedido-${safeName(opts.numero || 'ohbelleza')}.pdf`;
  const text = opts.numero
    ? `Pedido ${opts.numero} — Oh Belleza`
    : 'Pedido Oh Belleza';

  if (Capacitor.isNativePlatform()) {
    try {
      await shareNativeFile(blob, fileName, text);
      return { ok: true, via: 'native' };
    } catch (err) {
      console.warn('share native pdf failed', err);
    }
  }

  const file = new File([blob], fileName, { type: 'application/pdf' });
  if (typeof navigator !== 'undefined' && navigator.canShare && navigator.canShare({ files: [file] })) {
    await navigator.share({ files: [file], title: fileName, text });
    return { ok: true, via: 'web-share' };
  }

  downloadBlob(blob, fileName);
  const { openPedidoWhatsApp } = await import('./whatsappPedido');
  openPedidoWhatsApp({
    telefono: opts.clienteTelefono || opts.telefono,
    numero: opts.numero,
    clienteNombre: opts.clienteNombre,
    items: opts.items,
    subtotal: opts.subtotal,
    includeTotal: true,
    offline: Boolean(opts.offline),
  });
  return { ok: true, via: 'download+wa-text' };
}
